import logo from '@/assets/logo-with-text-deep.png'
import OrbitAvatar from '@/components/OrbitAvatar'
import TermsModal from '@/components/TermsModal/TermsModal'
import { acceptInvitation, getInvitationInfo, rejectInvitation } from '@/services/member/member'
import { getTerms } from '@/services/terms/terms.service'
import { SwapRightOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Alert, Button, Checkbox, Divider, Form, Result, Space, Spin, Typography } from 'antd'
import React, { memo, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useParams, useSearchParams } from 'react-router-dom'
import styles from './index.module.less'

export default memo(function InviteConfirm() {
  const { token } = useParams()
  const [searchParams] = useSearchParams()
  const [valid, setValid] = useState<boolean>()
  const [msg, setMsg] = useState('')
  const navigate = useNavigate()
  const { data, error } = useRequest(() => getInvitationInfo(token!))
  const isResetPwd = searchParams.get('resetpassword')
  const { run } = useRequest(() => acceptInvitation(token!, data!.termsId), {
    manual: true,
    onSuccess: () => {
      if (isResetPwd === 'true') {
        return navigate('/entrance/change-password')
      }
      navigate('/')
    }
  })

  useEffect(() => {
    if (error) {
      setValid(false)
    }
  }, [error])

  useEffect(() => {
    if (!data) {
      return
    }
    setValid(data.valid !== undefined ? data.valid : true)
    if (data.valid === false) {
      // eslint-disable-next-line quotes
      setMsg(`This invitation isn't valid.`)
      return
    }
    if (!data.expireTime) {
      return
    }
    const diff = data.expireTime - new Date().getTime()
    if (diff < 0) {
      setValid(false)
      setMsg('This invitation is overdue.')
      return
    }
    setValid(true)
    const days = diff / (1000 * 3600 * 24)
    const text =
      days > 2
        ? `The invitation will remain active for ${Math.floor(days)}-days and can be accepted later.`
        : 'The invitation is about to expire.'
    setMsg(text)
  }, [data])

  const reject = () => {
    rejectInvitation(token!).then(() => {
      navigate('/')
    })
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <header>
          <img height={24} src={logo} />
        </header>
        {!valid && (
          <div className={styles.join}>
            {valid === undefined && <Spin />}
            {valid !== undefined && (
              <Result
                title='This invitation is overdue.'
                extra={
                  <Button
                    type='primary'
                    key='goHome'
                    onClick={() => {
                      navigate('/notification')
                    }}
                  >
                    Go Notification
                  </Button>
                }
              />
            )}
          </div>
        )}
        {valid && (
          <Form
            className={styles.join}
            onFinish={() => {
              run()
            }}
          >
            <Space>
              <OrbitAvatar size={70} avatar={data?.orgAvatar!} name={data?.orgName} />
              {data?.type !== 'organizations' && (
                <>
                  <SwapRightOutlined style={{ fontSize: 35, color: '#C2C2C2' }} />
                  <OrbitAvatar size={60} avatar={data?.avatar!} name={data?.name} />
                </>
              )}
            </Space>

            <h2 className={'mt-3 mb-3'}>
              You’ve been invited to the <strong>{data?.orgName}</strong>
              {data?.type !== 'organizations' && data?.name && <strong> {data?.name} </strong>}
            </h2>

            <Form.Item
              name={'accept'}
              valuePropName='checked'
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error('Should accept agreement'))
                }
              ]}
            >
              <Checkbox>
                I review and accept the Terms
                <Typography.Link
                  underline
                  className={'ml-1'}
                  onClick={() => {
                    getTerms(data?.termsId!).then((terms) => {
                      TermsModal.show({
                        terms
                      })
                    })
                  }}
                >
                  terms
                </Typography.Link>
              </Checkbox>
            </Form.Item>

            <Button disabled={!valid} htmlType={'submit'} type={'primary'} style={{ width: '160px' }}>
              Join
            </Button>

            <Button
              disabled={!valid}
              onClick={reject}
              htmlType={'button'}
              type={'default'}
              style={{ width: '160px', marginTop: 20 }}
            >
              Decline
            </Button>

            <Divider plain>Or</Divider>

            <Button
              htmlType={'button'}
              type='text'
              onClick={() => {
                navigate('/')
              }}
            >
              ignore
            </Button>
            {msg && <Alert message={msg} type='warning' />}
          </Form>
        )}
      </div>
    </div>
  )
})
